import { useState, useEffect, useRef } from 'react';
import type { Room } from 'colyseus.js';
import { Card } from './Card';
import { BuyInModal } from './BuyInModal';
import { DefaultAvatar } from './DefaultAvatar';
import { parseCardStr, evaluateHand } from '../lib/cardUtils.js';
import type { PokerState, SeatState } from '../lib/pokerSchema';
import { playSound } from '../lib/sounds.js';

/* ─── Seat, Bet-chip & Dealer-button positions (% of game area) ─── */
const SEAT_POS: Record<number, { x: number; y: number }[]> = {
  2: [
    { x: 50, y: 15 },
    { x: 50, y: 78 },
  ],
  6: [
    { x: 50, y: 15 },
    { x: 83, y: 27 },
    { x: 83, y: 61 },
    { x: 50, y: 78 },
    { x: 17, y: 61 },
    { x: 17, y: 27 },
  ],
};

const BET_POS: Record<number, { x: number; y: number }[]> = {
  2: [
    { x: 50, y: 28 },
    { x: 50, y: 64 },
  ],
  6: [
    { x: 60, y: 24 },
    { x: 76, y: 33 },
    { x: 76, y: 55 },
    { x: 60, y: 70 },
    { x: 24, y: 55 },
    { x: 24, y: 33 },
  ],
};

const DEALER_POS: Record<number, { x: number; y: number }[]> = {
  2: [
    { x: 41, y: 22 },
    { x: 41, y: 70 },
  ],
  6: [
    { x: 39, y: 24 },
    { x: 75, y: 30 },
    { x: 75, y: 58 },
    { x: 39, y: 70 },
    { x: 25, y: 58 },
    { x: 25, y: 30 },
  ],
};

function seatPos(i: number, max: number) {
  if (SEAT_POS[max]) return SEAT_POS[max][i] ?? { x: 50, y: 50 };
  const a = (i / max) * 2 * Math.PI - Math.PI / 2;
  return { x: 50 + 38 * Math.cos(a), y: 46 + 34 * Math.sin(a) };
}

function betPos(i: number, max: number) {
  if (BET_POS[max]) return BET_POS[max][i] ?? { x: 50, y: 50 };
  const a = (i / max) * 2 * Math.PI - Math.PI / 2;
  return { x: 50 + 20 * Math.cos(a), y: 46 + 16 * Math.sin(a) };
}

function dealerPos(i: number, max: number) {
  if (DEALER_POS[max]) return DEALER_POS[max][i] ?? { x: 50, y: 46 };
  const a = (i / max) * 2 * Math.PI - Math.PI / 2;
  return { x: 50 + 28 * Math.cos(a), y: 46 + 24 * Math.sin(a) };
}

/* ═══════════════════════════════════════════════════════════
   CommunityCard — animated reveal from above
   ═══════════════════════════════════════════════════════════ */

function CommunityCard({ cardStr, className }: { cardStr: string; className?: string }) {
  const [appeared, setAppeared] = useState(false);
  useEffect(() => {
    // Two-rAF trick: let the browser paint the initial (hidden) state first,
    // then flip `appeared` so the CSS transition fires.
    let id1: number;
    let id2: number;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setAppeared(true));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, []);

  const { suit, rank } = parseCardStr(cardStr);
  return (
    <div
      style={{
        transform: appeared ? 'translateY(0)' : 'translateY(-48px)',
        opacity: appeared ? 1 : 0,
        transition: 'transform 0.22s cubic-bezier(0.22,1,0.36,1), opacity 0.18s ease',
      }}
    >
      <Card
        suit={suit}
        rank={rank as '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A'}
        className={className}
      />
    </div>
  );
}

const CARD_STAGGER_MS = 140;

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */

export interface PokerTableViewProps {
  room: Room<PokerState>;
  userId: string;
  balance: number;
  tableConfig: { minBuyIn: number; maxBuyIn: number; smallBlind: number; bigBlind: number; maxSeats?: number; stakes: string };
  /** Navigate back to the lobby without disconnecting from the room */
  onGoToLobby: () => void;
  /** Fully disconnect and leave this table */
  onLeaveTable: () => void;
  /** Called when balance changes (e.g. after sit or leave seat) so parent can reload profile */
  onBalanceChange?: (balanceDollars: number) => void;
  /** Called when the server closes the table (e.g. inactivity) */
  onTableClosing?: (reason?: string) => void;
}

export function PokerTableView({
  room,
  userId,
  balance,
  tableConfig,
  onGoToLobby,
  onLeaveTable,
  onBalanceChange,
  onTableClosing,
}: PokerTableViewProps) {
  const [state, setState] = useState(room.state);
  const [holeCards, setHoleCards] = useState<string[]>([]);
  const [balanceState, setBalanceState] = useState(balance);
  const pendingSitBuyInRef = useRef<number>(0);
  const [buyInSeat, setBuyInSeat] = useState<number | null>(null);
  const [showPostBBChoice, setShowPostBBChoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastShowdown, setLastShowdown] = useState<{
    winnerNames: string[];
    winnerSeats: number[];
    handNames: string[];
    amounts: number[];
  } | null>(null);
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [sitOutPending, setSitOutPending] = useState<'nextHand' | 'nextBB' | null>(null);
  const showdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Community card reveal animation state
  const [visibleCardCount, setVisibleCardCount] = useState(0);
  const prevCardCountRef = useRef(0);

  // Action labels shown above each player (e.g. "Check", "Raise", "Fold")
  const [actionLabels, setActionLabels] = useState<Map<number, string>>(new Map());
  const prevSnapRef = useRef<{
    turnSeat: number;
    phase: string;
    tableBet: number;
    seats: Map<number, { currentBet: number; status: string }>;
  } | null>(null);
  const labelClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lowTimeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [revealedHands, setRevealedHands] = useState<Map<number, { holeCards: string[]; handName: string }>>(new Map());

  /* ── Proportional scaling for small viewports ── */
  const mainRef = useRef<HTMLDivElement>(null);
  const [uiScale, setUiScale] = useState(1);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const update = () => {
      const s = Math.min(el.clientWidth / 1800, el.clientHeight / 900);
      setUiScale(Math.max(s, 0.4));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Sync balance from parent (e.g. when joining table) ── */
  useEffect(() => {
    setBalanceState(balance);
  }, [balance]);

  /* ── State sync ── */
  useEffect(() => {
    setState(room.state);
    const handler = () => {
      setState({ ...room.state, _v: Date.now() } as PokerState & { _v?: number });
    };
    room.onStateChange(handler);
    return () => room.onStateChange.remove(handler);
  }, [room]);

  const phase = (state.phase ?? 'waiting') as string;

  // Stable refs for callbacks — prevents the message-handler effect from
  // re-running (and double-registering handlers) when parent re-renders with
  // new inline function references.  The effect itself only depends on `room`.
  const onBalanceChangeRef = useRef(onBalanceChange);
  useEffect(() => { onBalanceChangeRef.current = onBalanceChange; }, [onBalanceChange]);
  const onTableClosingRef = useRef(onTableClosing);
  useEffect(() => { onTableClosingRef.current = onTableClosing; }, [onTableClosing]);
  const onLeaveTableRef = useRef(onLeaveTable);
  useEffect(() => { onLeaveTableRef.current = onLeaveTable; }, [onLeaveTable]);

  useEffect(() => {
    room.onMessage('holeCards', (msg: { cards: string[] }) => setHoleCards(msg.cards));
    room.onMessage('balance', (msg: { balance: number }) => {
      const cents = Math.round(msg.balance * 100);
      setBalanceState(cents);
      onBalanceChangeRef.current?.(msg.balance);
    });
    room.onMessage('error', (msg: { message: string }) => {
      setError(msg.message);
      if (pendingSitBuyInRef.current > 0) {
        const revert = pendingSitBuyInRef.current;
        pendingSitBuyInRef.current = 0;
        setBalanceState((prev) => {
          const reverted = prev + revert;
          onBalanceChangeRef.current?.(reverted / 100);
          return reverted;
        });
      }
    });
    room.onMessage('sitOk', () => {
      pendingSitBuyInRef.current = 0;
      setBuyInSeat(null);
      // BB dialog is now driven by mySeat.waitingForBB via schema (see useEffect below)
    });
    room.onMessage('sitOutOk', (msg: { mode: 'nextHand' | 'nextBB' }) => {
      setSitOutPending(msg.mode);
    });
    room.onMessage('sitOutCancelled', () => setSitOutPending(null));
    room.onMessage('setReadyOk', () => { /* schema state drives all display changes */ });
    room.onMessage('setAwayOk', () => { /* schema state drives all display changes */ });
    room.onMessage('tableClosing', (msg: { reason?: string }) => {
      if (onTableClosingRef.current) {
        onTableClosingRef.current(msg.reason);
      } else {
        onLeaveTableRef.current();
      }
    });
    room.onMessage(
      'revealHands',
      (msg: { revealedHands: Array<{ seatIndex: number; holeCards: string[]; handName: string }> }) => {
        setRevealedHands((prev) => {
          const next = new Map(prev);
          for (const r of msg.revealedHands) {
            next.set(r.seatIndex, { holeCards: r.holeCards, handName: r.handName });
          }
          return next;
        });
      }
    );
    room.onMessage(
      'showdownResult',
      (msg: {
        winnerNames: string[];
        winnerSeats?: number[];
        handNames: string[];
        amounts: number[];
        revealedHands?: Array<{ seatIndex: number; holeCards: string[]; handName: string }>;
      }) => {
        if (msg.revealedHands) {
          setRevealedHands((prev) => {
            const next = new Map(prev);
            for (const r of msg.revealedHands!) {
              next.set(r.seatIndex, { holeCards: r.holeCards, handName: r.handName });
            }
            return next;
          });
        }
        if (showdownTimerRef.current) clearTimeout(showdownTimerRef.current);
        // Play win sound only when the local player is among the winners
        if ((msg.winnerSeats ?? []).some((si: number) => {
          const s = room.state.seats?.get?.(String(si));
          return s?.userId === userId;
        })) {
          playSound('win');
        }
        setLastShowdown({
          winnerNames: msg.winnerNames,
          winnerSeats: msg.winnerSeats ?? [],
          handNames: msg.handNames,
          amounts: msg.amounts,
        });
        // No client-side timeout — winner animation persists until next hand
        // (cleared by the phase effect when phase changes to countdown/preflop/waiting)
      }
    );
    return () => {
      if (showdownTimerRef.current) clearTimeout(showdownTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]); // intentionally omit callback deps — refs above keep them current

  /* ── Derived values ── */
  const mySeatIndex =
    Array.from(state.seats?.values?.() ?? []).find((s: { userId: string }) => s.userId === userId)
      ?.seatIndex ?? -1;

  const inHandPhase = phase === 'preflop' || phase === 'flop' || phase === 'turn' || phase === 'river';
  const showBoard = inHandPhase || phase === 'showdown' || lastShowdown !== null || revealedHands.size > 0;

  const isMyTurn =
    inHandPhase && state.currentTurnSeat === mySeatIndex;

  const mySeat = state.seats?.get?.(String(mySeatIndex));
  const pot = Number(state.pot) || 0;
  const currentBet = state.currentBet ?? 0;
  const minRaise = state.minRaise ?? 0;
  const myStack = mySeat?.stack ?? 0;
  const myCurrentBet = mySeat?.currentBet ?? 0;
  const minRaiseTo = currentBet + minRaise;
  const maxRaiseTo = mySeat ? myStack + myCurrentBet : minRaiseTo;

  const callAmount = (() => {
    if (!mySeat || currentBet <= mySeat.currentBet) return 0;
    return Math.min(currentBet - mySeat.currentBet, mySeat.stack);
  })();

  useEffect(() => {
    if (isMyTurn) {
      setRaiseAmount(minRaiseTo <= maxRaiseTo ? minRaiseTo : maxRaiseTo);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTurn]);

  // BB dialog is driven entirely by the schema waitingForBB flag
  useEffect(() => {
    setShowPostBBChoice(Boolean(mySeat?.waitingForBB));
  }, [mySeat?.waitingForBB]);

  // Clear pending sit-out indicator once player transitions to sitting-out
  useEffect(() => {
    if (mySeat?.status === 'sitting-out') {
      setSitOutPending(null);
    }
  }, [mySeat?.status]);

  useEffect(() => {
    if (phase === 'countdown' || phase === 'preflop') {
      setLastShowdown(null);
      setRevealedHands(new Map());
      // Reset card animation so the next deal triggers staggered reveal
      setVisibleCardCount(0);
      prevCardCountRef.current = 0;
    }
    // When game ends (waiting) with 1 player left, clear all card state so the remaining player
    // appears as if they just sat down (no cards visible).
    if (phase === 'waiting') {
      setLastShowdown(null);
      setRevealedHands(new Map());
      setHoleCards([]);
      setVisibleCardCount(0);
      prevCardCountRef.current = 0;
    }
  }, [phase]);

  // Derive community cards early so the stagger effect can reference it
  const communityCards = Array.isArray(state.communityCards)
    ? state.communityCards
    : Array.from(state.communityCards ?? []);

  // ── Community card staggered reveal ─────────────────────────────────────────
  useEffect(() => {
    const newCount = communityCards.length;
    const oldCount = prevCardCountRef.current;
    if (newCount <= oldCount) return; // cards were cleared; reset already handled by phase effect

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = oldCount; i < newCount; i++) {
      const delay = (i - oldCount) * CARD_STAGGER_MS;
      timers.push(
        setTimeout(() => {
          setVisibleCardCount(i + 1);
          playSound('placeCard');
        }, delay)
      );
    }
    prevCardCountRef.current = newCount;

    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityCards.length]);

  // ── Detect player actions → update labels + play sounds ────────────────────
  useEffect(() => {
    const curTurn = (state.currentTurnSeat ?? -1) as number;
    const curPhase = phase;
    const prev = prevSnapRef.current;

    if (prev) {
      const phaseMoved = prev.phase !== curPhase;
      const turnMoved = prev.turnSeat !== curTurn && prev.turnSeat >= 0;

      if (turnMoved || (phaseMoved && prev.turnSeat >= 0)) {
        const actingSeat = prev.turnSeat;
        const prevSeatData = prev.seats.get(actingSeat);
        const curSeatState = state.seats?.get?.(String(actingSeat));

        const prevStatus = prevSeatData?.status ?? '';
        const nowStatus  = curSeatState?.status  ?? '';
        const prevBet    = prevSeatData?.currentBet ?? 0;
        const nowBet     = curSeatState?.currentBet  ?? 0;

        let label = '';

        if (nowStatus === 'folded' && prevStatus !== 'folded') {
          label = 'Fold';
          playSound('fold');
        } else if (nowStatus === 'all-in' && prevStatus !== 'all-in') {
          label = 'All In';
          playSound('chip');
        } else if (phaseMoved) {
          // Round-ending action: bets already reset to 0 so compare prev state only.
          // If the player's pre-action bet already matched the table bet → they checked;
          // otherwise they must have called to close the action.
          label = prevBet >= prev.tableBet ? 'Check' : 'Call';
          playSound(label === 'Check' ? 'check' : 'chip');
        } else if (nowBet > prevBet) {
          if (prev.tableBet === 0) {
            label = 'Bet';
          } else if (nowBet > prev.tableBet) {
            label = 'Raise';
          } else {
            label = 'Call';
          }
          playSound('chip');
        } else {
          label = 'Check';
          playSound('check');
        }

        if (phaseMoved) {
          // Show the label briefly then clear all when the new round renders
          setActionLabels((prev) => { const n = new Map(prev); if (label) n.set(actingSeat, label); return n; });
          if (labelClearTimerRef.current) clearTimeout(labelClearTimerRef.current);
          labelClearTimerRef.current = setTimeout(() => {
            setActionLabels(new Map());
            labelClearTimerRef.current = null;
          }, 1400);
        } else {
          setActionLabels((prevLabels) => {
            const next = new Map(prevLabels);
            if (label) next.set(actingSeat, label);
            // Clear label for whoever is now acting (action returned to them)
            if (curTurn >= 0) next.delete(curTurn);
            return next;
          });
        }
      } else if (phaseMoved) {
        // Phase changed without a detectable turn move (e.g. hand start) — clear labels
        if (labelClearTimerRef.current) clearTimeout(labelClearTimerRef.current);
        setActionLabels(new Map());
      }
    }

    // Snapshot current state for next comparison
    prevSnapRef.current = {
      turnSeat: curTurn,
      phase: curPhase,
      tableBet: (state.currentBet ?? 0) as number,
      seats: new Map(
        Array.from(state.seats?.entries?.() ?? []).map(([k, v]: [string, SeatState]) => [
          Number(k),
          { currentBet: v.currentBet ?? 0, status: v.status ?? '' },
        ])
      ),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ── Low-time warning sound (< 5 s left on hero's turn) ──────────────────────
  useEffect(() => {
    if (lowTimeIntervalRef.current) {
      clearInterval(lowTimeIntervalRef.current);
      lowTimeIntervalRef.current = null;
    }
    if (!isMyTurn) return;
    const turnEnd = Number(state.turnEndsAt ?? 0);
    if (turnEnd <= 0) return;

    lowTimeIntervalRef.current = setInterval(() => {
      const remaining = turnEnd - Date.now();
      if (remaining > 0 && remaining <= 5000) {
        playSound('lowTime');
      }
    }, 1000);

    return () => {
      if (lowTimeIntervalRef.current) {
        clearInterval(lowTimeIntervalRef.current);
        lowTimeIntervalRef.current = null;
      }
    };
  }, [isMyTurn, state.turnEndsAt]);

  const requestedHoleCardsRef = useRef(false);
  useEffect(() => {
    if (!inHandPhase) requestedHoleCardsRef.current = false;
    else if (mySeatIndex >= 0 && holeCards.length === 0 && !requestedHoleCardsRef.current) {
      requestedHoleCardsRef.current = true;
      room.send('requestHoleCards');
    }
  }, [inHandPhase, mySeatIndex, holeCards.length, room]);

  /* ── Seat rotation ── */
  const maxSeats = state.tableConfig?.maxSeats ?? tableConfig.maxSeats ?? 6;
  const seatsFromState = Array.from(state.seats?.values?.() ?? []).sort(
    (a: { seatIndex: number }, b: { seatIndex: number }) => a.seatIndex - b.seatIndex
  );
  const bottomDisplayIndex = Math.floor(maxSeats / 2);
  const displayIndexToSeat = (di: number): number => {
    if (mySeatIndex < 0) return di;
    return (di - bottomDisplayIndex + mySeatIndex + maxSeats) % maxSeats;
  };
  const seatsArray: SeatState[] = Array.from({ length: maxSeats }, (_, di) => {
    const actual = displayIndexToSeat(di);
    const existing = seatsFromState.find((s: { seatIndex: number }) => s.seatIndex === actual);
    if (existing) return existing as SeatState;
    return { seatIndex: actual, userId: '', displayName: '', stack: 0, currentBet: 0, status: 'empty' as const, holeCards: [], isDealer: false, isSmallBlind: false, isBigBlind: false };
  });

  const handleSit = (seatIndex: number, amount: number) => {
    pendingSitBuyInRef.current = amount;
    setBalanceState((prev) => {
      const next = prev - amount;
      onBalanceChange?.(next / 100);
      return next;
    });
    room.send('sit', { seatIndex, buyIn: amount });
  };

  const stakesDisplay = tableConfig.stakes || `$${(tableConfig.smallBlind / 100).toFixed(2)} / $${(tableConfig.bigBlind / 100).toFixed(2)}`;

  /* ═══ Render ═══ */
  return (
    <div className="h-full flex flex-col bg-[#080810] overflow-hidden select-none">
      {/* ── Header ── */}
      <header className="flex items-center justify-between px-6 py-2.5 border-b border-white/5 bg-[#0c0c14]/90 backdrop-blur-sm shrink-0 z-40">
        <button
          type="button"
          onClick={onGoToLobby}
          className="flex items-center gap-1.5 text-text-muted text-sm hover:text-text transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11L5 7l4-4" />
          </svg>
          Lobby
        </button>
        <div className="flex items-center gap-4">
          <span className="text-primary font-semibold tabular-nums">${(balanceState / 100).toFixed(2)}</span>
          <span className="text-text-muted text-sm">{stakesDisplay} NLH</span>
        </div>
        <button
          type="button"
          onClick={onLeaveTable}
          className={
            'px-3 py-1.5 rounded-lg text-sm font-medium transition-all ' +
            'border border-white/[0.12] bg-white/[0.04] text-white/60 ' +
            'hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 ' +
            'active:scale-95'
          }
          title="Disconnect and leave this table"
        >
          Leave Table
        </button>
      </header>

      {error && (
        <div className="px-6 py-2 bg-error/20 text-error text-sm text-center shrink-0" role="alert">{error}</div>
      )}

      {/* ── Game Area ── */}
      <main ref={mainRef} className="flex-1 relative overflow-hidden min-h-0">
        {/* Centering shell: visual-sized box centered in the viewport */}
        <div
          className="absolute left-1/2 top-1/2"
          style={{
            width: `${1800 * uiScale}px`,
            height: `${900 * uiScale}px`,
            transform: 'translate(-50%, -50%)',
          }}
        >
        {/* Fixed 1800x900 coordinate space, uniformly scaled */}
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            transform: `scale(${uiScale})`,
            width: '1800px',
            height: '900px',
          }}
        >

        {/* Table oval ------------------------------------------------ */}
        <div className="absolute" style={{ top: '13%', bottom: '20%', left: '10%', right: '10%' }}>
          {/* Rail */}
          <div className="absolute -inset-3 rounded-[50%] bg-gradient-to-b from-[#3a2818] via-[#1c120a] to-[#3a2818]" />
          {/* Felt */}
          <div className="absolute inset-0 rounded-[50%] bg-gradient-to-br from-emerald-800/95 via-emerald-900 to-[#0c3a24] shadow-[inset_0_4px_60px_rgba(0,0,0,0.35)]" />
          {/* Inner trim */}
          <div className="absolute inset-[18px] rounded-[50%] border border-white/[0.06] pointer-events-none" />
          {/* Center watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-white/[0.07] text-base sm:text-xl font-bold tracking-[0.3em] uppercase">
              No Limit Hold&apos;em
            </span>
          </div>
        </div>

        {/* Pot display ----------------------------------------------- */}
        {showBoard && (
          <div className="absolute z-10 left-1/2 -translate-x-1/2" style={{ top: '33%' }}>
            <div className="flex items-center gap-2 px-5 py-2 rounded-full bg-black/55 backdrop-blur-sm border border-white/[0.08]">
              <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">Total Pot:</span>
              <span className="text-white font-bold text-base tabular-nums">${(pot / 100).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Community cards ------------------------------------------- */}
        {showBoard && communityCards.length > 0 && (
          <div className="absolute z-10 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ top: '48%' }}>
            <div className="flex gap-3" style={{ overflow: 'visible' }}>
              {communityCards.map((cardStr, i) => {
                if (typeof cardStr !== 'string') return null;
                // During active deal animation only show up to visibleCardCount.
                // Once all cards are shown (visibleCardCount >= communityCards.length)
                // we render all — handles showdown / revealed hands replay.
                if (visibleCardCount < communityCards.length && i >= visibleCardCount) return null;
                return (
                  <CommunityCard
                    key={`${i}-${cardStr}`}
                    cardStr={cardStr}
                    className="w-[5.5rem] sm:w-[6rem] text-[12px] shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Bet chips ------------------------------------------------- */}
        {seatsArray.map((seat, i) => {
          // Hide individual bets once the hand is over (showdown result or showdown phase)
          if (phase === 'showdown' || lastShowdown !== null) return null;
          if (seat.currentBet <= 0 || seat.status === 'empty') return null;
          const p = betPos(i, maxSeats);
          return (
            <div
              key={`bet-${seat.seatIndex}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-[22]"
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
            >
              <div className="flex items-end gap-2">
                <ChipStack amount={seat.currentBet} bigBlind={tableConfig.bigBlind} />
                <div className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/[0.06]">
                  <span className="text-white text-[18px] font-bold tabular-nums">${(seat.currentBet / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Dealer button puck on table -------------------------------- */}
        {showBoard && (() => {
          const dealerActual = state.dealerSeat ?? -1;
          const di = seatsArray.findIndex(s => s.seatIndex === dealerActual);
          if (di < 0) return null;
          const dp = dealerPos(di, maxSeats);
          return (
            <div
              className="absolute -translate-x-1/2 -translate-y-1/2 z-[9]"
              style={{ left: `${dp.x}%`, top: `${dp.y}%` }}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#f5f0e0] to-[#d4c9a8] border-[2.5px] border-[#bfb68a] shadow-[0_2px_8px_rgba(0,0,0,0.4)] flex items-center justify-center">
                <span className="text-[#1a1a1a] text-base font-extrabold leading-none">D</span>
              </div>
            </div>
          );
        })()}

        {/* Seats ------------------------------------------------------ */}
        {(() => {
          const winnerProfits = new Map<number, number>();
          if (lastShowdown) {
            for (let i = 0; i < lastShowdown.winnerSeats.length; i++) {
              winnerProfits.set(lastShowdown.winnerSeats[i], lastShowdown.amounts[i] ?? 0);
            }
          }
          return seatsArray.map((seat, i) => {
            const p = seatPos(i, maxSeats);
            const isHero = seat.userId === userId && mySeatIndex >= 0;
            const revealed = revealedHands.get(seat.seatIndex);
            const effectiveHoleCards = isHero ? holeCards : (revealed ? revealed.holeCards : []);
            const effectiveCommunity = (isHero || revealed) ? communityCards.filter((c): c is string => typeof c === 'string') : [];
            return (
              <div
                key={seat.seatIndex}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              >
                {seat.status === 'empty' ? (
                  mySeatIndex < 0 ? <EmptySeat onClick={() => setBuyInSeat(seat.seatIndex)} /> : null
                ) : (
                  <PlayerSeat
                    seat={seat}
                    isTurn={(state.currentTurnSeat ?? -1) === seat.seatIndex && inHandPhase}
                    turnEndsAt={Number(state.turnEndsAt ?? 0)}
                    isHero={isHero}
                    holeCards={effectiveHoleCards}
                    isInHand={showBoard}
                    communityCards={effectiveCommunity}
                    isWinner={lastShowdown?.winnerSeats?.includes(seat.seatIndex) ?? false}
                    winnerProfit={winnerProfits.get(seat.seatIndex)}
                    actionLabel={actionLabels.get(seat.seatIndex)}
                  />
                )}
              </div>
            );
          });
        })()}

        </div>{/* end scaled wrapper */}
        </div>{/* end centering shell */}

        {/* Action panel — bottom-right, only on player's turn */}
        {mySeatIndex >= 0 && inHandPhase && isMyTurn && (() => {
          const panelScale = Math.min(1, Math.max(0.55, uiScale * 1.05));
          return (
            <div
              className="absolute bottom-2 right-2 z-30 origin-bottom-right"
              style={{ transform: `scale(${panelScale})` }}
            >
              <ActionPanel
                room={room}
                callAmount={callAmount}
                minRaiseTo={minRaiseTo}
                maxRaiseTo={maxRaiseTo}
                raiseAmount={raiseAmount}
                setRaiseAmount={setRaiseAmount}
                pot={pot}
                myCurrentBet={myCurrentBet}
                isMyTurn={isMyTurn}
                phase={phase}
                bigBlind={tableConfig.bigBlind}
                currentBet={currentBet}
              />
            </div>
          );
        })()}

        {/* ── Sit-out controls — bottom-left, for players actively in a hand ── */}
        {mySeatIndex >= 0 && inHandPhase && mySeat?.status !== 'sitting-out' && !mySeat?.waitingForBB && (
          <div className="absolute bottom-2 left-2 z-30 flex gap-2">
            <button
              type="button"
              onClick={() => {
                if (sitOutPending === 'nextHand') {
                  room.send('cancelSitOut');
                } else {
                  room.send('sitOutNextHand');
                }
              }}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                sitOutPending === 'nextHand'
                  ? 'bg-amber-500/20 border-amber-500/60 text-amber-400'
                  : 'bg-white/[0.08] border-white/[0.12] text-text-muted hover:bg-white/[0.14] hover:text-white'
              }`}
            >
              {sitOutPending === 'nextHand' ? '✓ Sit out next hand' : 'Sit out next hand'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (sitOutPending === 'nextBB') {
                  room.send('cancelSitOut');
                } else {
                  room.send('sitOutNextBB');
                }
              }}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                sitOutPending === 'nextBB'
                  ? 'bg-amber-500/20 border-amber-500/60 text-amber-400'
                  : 'bg-white/[0.08] border-white/[0.12] text-text-muted hover:bg-white/[0.14] hover:text-white'
              }`}
            >
              {sitOutPending === 'nextBB' ? '✓ Sit out next big blind' : 'Sit out next big blind'}
            </button>
          </div>
        )}

        {/* ── Set Away — bottom-center, for seated players between hands ── */}
        {mySeatIndex >= 0 && !inHandPhase && mySeat?.status === 'seated' && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30">
            <button
              type="button"
              onClick={() => room.send('setAway')}
              className="px-4 py-2 rounded-lg bg-white/[0.08] border border-white/[0.12] text-text-muted text-sm font-medium hover:bg-white/[0.14] hover:text-white transition-colors"
            >
              Set Away
            </button>
          </div>
        )}

        {/* ── Away player — "I'm Ready" button, bottom-center, prominent ── */}
        {mySeatIndex >= 0 && mySeat?.status === 'sitting-out' && !mySeat?.isReady && !mySeat?.waitingForBB && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30">
            <button
              type="button"
              onClick={() => room.send('setReady')}
              className="px-8 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              I&apos;m Ready
            </button>
          </div>
        )}

        {/* ── Ready player, game not running — "I'm Away" button ── */}
        {mySeatIndex >= 0 && mySeat?.status === 'sitting-out' && mySeat?.isReady && !inHandPhase && !mySeat?.waitingForBB && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30">
            <button
              type="button"
              onClick={() => room.send('setAway')}
              className="px-4 py-2 rounded-lg bg-white/[0.08] border border-white/[0.12] text-text-muted text-sm font-medium hover:bg-white/[0.14] hover:text-white transition-colors"
            >
              I&apos;m Away
            </button>
          </div>
        )}

        {/* ── Ready player, game running — queued label ── */}
        {mySeatIndex >= 0 && mySeat?.status === 'sitting-out' && mySeat?.isReady && inHandPhase && !mySeat?.waitingForBB && (
          <div className="absolute bottom-2 right-2 z-30">
            <div className="px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-medium">
              Queued for next hand
            </div>
          </div>
        )}

        {/* ── Post-BB choice — shown when waitingForBB is true (driven by schema) ── */}
        {showPostBBChoice && mySeat?.waitingForBB && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 flex gap-3 px-4 py-3 rounded-xl bg-black/80 backdrop-blur-md border border-white/[0.08]">
            <span className="text-text-muted text-sm self-center">Post big blind to play:</span>
            <button
              type="button"
              onClick={() => room.send('postBB')}
              disabled={(mySeat?.stack ?? 0) < tableConfig.bigBlind}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Post BB to play now
            </button>
            <button
              type="button"
              onClick={() => room.send('waitForBB')}
              className="px-4 py-2 rounded-lg bg-white/[0.08] border border-white/[0.12] text-text-muted text-sm font-medium hover:bg-white/[0.14] hover:text-white transition-colors"
            >
              Wait for BB
            </button>
          </div>
        )}
      </main>

      <BuyInModal
        isOpen={buyInSeat !== null}
        balance={balanceState}
        minBuyIn={tableConfig.minBuyIn}
        maxBuyIn={tableConfig.maxBuyIn}
        stakes={stakesDisplay}
        onConfirm={(amount) => buyInSeat !== null && handleSit(buyInSeat, amount)}
        onClose={() => setBuyInSeat(null)}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Empty Seat
   ═══════════════════════════════════════════════════════════ */

function EmptySeat({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
      aria-label="Sit here"
    >
      <div className="w-[8.5rem] h-[8.5rem] rounded-full bg-white/[0.04] border-2 border-dashed border-white/[0.12] flex items-center justify-center text-white/25 text-5xl group-hover:border-primary/50 group-hover:text-primary/50 group-hover:bg-primary/[0.06] transition-all duration-200">
        +
      </div>
      <span className="text-white/20 text-[11px] font-medium group-hover:text-primary/60 transition-colors">
        Sit
      </span>
    </button>
  );
}

const FOLD_ANIMATION_MS = 800;
/** Must match server TURN_TIMEOUT_MS for fuse sync */
const TURN_TIMEOUT_MS = 30000;

/* ═══════════════════════════════════════════════════════════
   Player Seat
   ═══════════════════════════════════════════════════════════ */

const ACTION_BADGE_COLORS: Record<string, string> = {
  Check:  '#475569', // slate-600
  Call:   '#1d4ed8', // blue-700
  Bet:    '#15803d', // green-700
  Raise:  '#b45309', // amber-700
  Fold:   '#7f1d1d', // red-900
  'All In': '#7f1d1d', // red-900
};

function PlayerSeat({
  seat,
  isTurn,
  turnEndsAt,
  isHero,
  holeCards,
  isInHand,
  communityCards = [],
  isWinner = false,
  winnerProfit,
  actionLabel,
}: {
  seat: SeatState;
  isTurn: boolean;
  turnEndsAt: number;
  isHero: boolean;
  holeCards: string[];
  isInHand: boolean;
  communityCards?: string[];
  isWinner?: boolean;
  winnerProfit?: number;
  actionLabel?: string;
}) {
  const isFolded = seat.status === 'folded';
  const isAllIn = seat.status === 'all-in';
  const isSittingOut = seat.status === 'sitting-out';
  const hasRevealed = holeCards.length > 0;
  const [foldAnimating, setFoldAnimating] = useState(false);
  const prevFoldedRef = useRef(false);

  /* Fuse progress driven by server turnEndsAt — survives tab sleep/background */
  const [fuseRemaining, setFuseRemaining] = useState(1);
  useEffect(() => {
    if (!isTurn || turnEndsAt <= 0) {
      setFuseRemaining(1);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, (turnEndsAt - Date.now()) / TURN_TIMEOUT_MS);
      setFuseRemaining(remaining);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [isTurn, turnEndsAt]);

  /* Detect transition to folded and start fold animation */
  useEffect(() => {
    if (isFolded && !prevFoldedRef.current) {
      prevFoldedRef.current = true;
      setFoldAnimating(true);
      const t = setTimeout(() => {
        setFoldAnimating(false);
      }, FOLD_ANIMATION_MS);
      return () => clearTimeout(t);
    }
    if (!isFolded) prevFoldedRef.current = false;
  }, [isFolded]);

  const showCards = isInHand && (!isFolded || foldAnimating) && !isSittingOut;
  const isFoldAnimating = isFolded && foldAnimating;

  const heroCardSize = 'w-[5rem] sm:w-[5.5rem]';
  const opponentCardSize = 'w-[4.25rem] sm:w-[4.75rem]';
  const cardSize = isHero ? heroCardSize : opponentCardSize;
  const stackDisplay = '$' + (seat.stack / 100).toFixed(2);
  const displayName = seat.displayName || 'Player';

  const avatarSize = isHero ? 'w-[9.5rem] h-[9.5rem]' : 'w-[8.5rem] h-[8.5rem]';

  const turnRing = isTurn
    ? ' ring-[3px] ring-amber-400/90 ring-offset-2 ring-offset-[#080810] shadow-[0_0_20px_rgba(250,204,21,0.3)]'
    : '';

  const winnerRing = isWinner && showCards ? ' animate-winner-glow' : '';

  const plateStyle = 'bg-gradient-to-b from-slate-700 to-slate-800 border border-white/[0.08]';

  const renderCards = () => {
    if (!showCards) return null;
    if (hasRevealed && holeCards.length > 0) {
      return holeCards.slice(0, 2).map((c, i) => {
        const { suit, rank } = parseCardStr(c);
        return (
          <Card
            key={i}
            suit={suit}
            rank={rank as '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A'}
            className={`${cardSize} text-[10px] shadow-lg transition-all duration-[700ms] ease-out ${i === 0 ? '-rotate-[8deg]' : 'rotate-[8deg] -ml-3'} ${isFoldAnimating ? (i === 0 ? '!-rotate-[28deg] !translate-y-[72px] !opacity-0' : '!rotate-[28deg] !translate-y-[72px] !opacity-0') : ''}`}
          />
        );
      });
    }
    return [0, 1].map((i) => (
      <Card
        key={i}
        faceDown
        suit="spades"
        rank="2"
        className={`${cardSize} shadow-lg transition-all duration-[700ms] ease-out ${i === 0 ? '-rotate-[8deg]' : 'rotate-[8deg] -ml-3'} ${isFoldAnimating ? (i === 0 ? '!-rotate-[28deg] !translate-y-[72px] !opacity-0' : '!rotate-[28deg] !translate-y-[72px] !opacity-0') : ''}`}
      />
    ));
  };

  const nameplate = (
    <div className="relative">
      <div className={'rounded-xl text-center px-4 py-2 min-w-[10rem] shadow-[0_4px_16px_rgba(0,0,0,0.4)] ' + plateStyle}>
        <p className={'text-[13px] font-semibold truncate max-w-[10rem] ' + (isHero ? 'text-emerald-400' : 'text-white')}>
          {displayName}
        </p>
        <p className="text-slate-400 text-[17px] font-medium tabular-nums mt-0.5">{stackDisplay}</p>
      </div>
      {isTurn && (
        <div className="absolute -bottom-1 left-0 right-0 h-1.5 rounded-full bg-slate-700 overflow-hidden">
          <div
            className="h-full w-full rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 shadow-[0_0_6px_rgba(251,191,36,0.8)] origin-left transition-none"
            style={{ transform: `scaleX(${fuseRemaining})` }}
          />
        </div>
      )}
    </div>
  );

  const handName = holeCards.length >= 2 && !isFolded ? evaluateHand(holeCards, communityCards) : '';

  const allInBadge = isAllIn
    ? <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold whitespace-nowrap shadow-md z-30">ALL IN</div>
    : null;

  // Action badge: shown above the player until the next betting round or their turn returns.
  // Suppressed when the winner badge is visible, or when ALL IN badge already covers the action.
  const showActionBadge = !!actionLabel && !isAllIn && !(isWinner && showCards);
  const actionBadge = showActionBadge
    ? (
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full px-3 py-0.5 rounded-full text-white text-[11px] font-bold whitespace-nowrap shadow-md z-30"
        style={{ background: ACTION_BADGE_COLORS[actionLabel!] ?? '#475569', marginTop: '-2px' }}
      >
        {actionLabel}
      </div>
    )
    : null;

  const isAway = isSittingOut && !seat.isReady;
  const isReadyWaiting = isSittingOut && seat.isReady;

  const sitOutBadge = isSittingOut
    ? (
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-0.5 rounded-full text-white text-[11px] font-bold whitespace-nowrap shadow-md z-30"
        style={{ background: isReadyWaiting ? '#b45309' : '#475569' }}
      >
        {isReadyWaiting ? 'Ready' : 'Away'}
      </div>
    )
    : null;

  const winnerBadge = isWinner && showCards
    ? (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-1">
          <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-amber-950 text-[12px] font-extrabold whitespace-nowrap shadow-[0_0_20px_rgba(251,191,36,0.6)] border border-amber-300/80 uppercase tracking-wider">
            Winner
          </div>
          {winnerProfit != null && winnerProfit > 0 && (
            <div className="px-3 py-0.5 rounded-full bg-emerald-500/90 text-white text-[13px] font-bold whitespace-nowrap shadow-md border border-emerald-400/60">
              +${(winnerProfit / 100).toFixed(2)}
            </div>
          )}
        </div>
      )
    : null;

  const foldLabel = isFolded
    ? <span className="mt-1 text-red-400/80 text-[12px] font-bold uppercase tracking-wide">Fold</span>
    : null;

  return (
    <div className={'flex flex-col items-center transition-opacity duration-500 ' + (isFolded ? 'opacity-45' : '') + (isAway ? ' opacity-50 grayscale' : '')}>
      <div className="relative flex flex-col items-center">
        <div className={'rounded-full overflow-hidden shadow-lg border-2 border-white/10 bg-slate-700/30 transition-all duration-500 z-0 ' + avatarSize + ' ' + turnRing + winnerRing}>
          <DefaultAvatar avatarUrl={null} className="w-full h-full" />
        </div>
        {isInHand && (
          <div className={'relative z-10 flex -mt-20' + (isWinner && showCards ? ' animate-winner-cards-pulse' : '')}>
            {showCards ? renderCards() : (
              <div className="flex invisible shrink-0" aria-hidden>
                <div className={`${cardSize} aspect-[5/7]`} />
                <div className={`${cardSize} -ml-3 aspect-[5/7]`} />
              </div>
            )}
          </div>
        )}
        <div className={(isInHand ? '-mt-4' : '-mt-3') + ' z-20'}>{nameplate}</div>
        {actionBadge}
        {allInBadge}
        {sitOutBadge}
        {winnerBadge}
      </div>
      {handName ? (
        <div className="-mt-2 z-30 px-2 rounded-full bg-emerald-600/80 shadow-md flex items-center justify-center" style={{ height: 22 }}>
          <span className="text-white text-[12px] font-bold uppercase tracking-wider leading-none" style={{ marginTop: 1 }}>{handName}</span>
        </div>
      ) : null}
      {foldLabel}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Chip Stack  (visual stack of poker chips next to bet amounts)
   ═══════════════════════════════════════════════════════════ */

const CHIP_COLORS: [number, string, string][] = [
  [10, '#34495e', '#1a252f'],
  [5,  '#2ecc71', '#1a7a42'],
  [2,  '#3498db', '#1c5a85'],
  [0,  '#e74c3c', '#8e2b20'],
];

function ChipStack({ amount, bigBlind }: { amount: number; bigBlind: number }) {
  const numBB = Math.max(1, Math.round(amount / Math.max(1, bigBlind)));
  const chipCount = Math.min(numBB, 8);
  const h = (chipCount - 1) * 4 + 8;

  const [, cLight, cDark] = CHIP_COLORS.find(([min]) => numBB >= min) ?? CHIP_COLORS[CHIP_COLORS.length - 1]!;

  return (
    <div className="relative shrink-0" style={{ width: 24, height: h }}>
      {Array.from({ length: chipCount }, (_, i) => (
        <div
          key={i}
          className="absolute left-0 rounded-full"
          style={{
            width: 24,
            height: 8,
            bottom: i * 4,
            background: `linear-gradient(to bottom, ${cLight}, ${cDark})`,
            borderTop: '1px solid rgba(255,255,255,0.3)',
            borderBottom: '1px solid rgba(0,0,0,0.5)',
            boxShadow: i === 0 ? '0 2px 4px rgba(0,0,0,0.3)' : undefined,
          }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Action Panel
   ═══════════════════════════════════════════════════════════ */

function ActionPanel({
  room,
  callAmount,
  minRaiseTo,
  maxRaiseTo,
  raiseAmount,
  setRaiseAmount,
  pot,
  myCurrentBet,
  isMyTurn,
  phase,
  bigBlind,
  currentBet,
}: {
  room: Room<PokerState>;
  callAmount: number;
  minRaiseTo: number;
  maxRaiseTo: number;
  raiseAmount: number;
  setRaiseAmount: (v: number | ((p: number) => number)) => void;
  pot: number;
  myCurrentBet: number;
  isMyTurn: boolean;
  phase: string;
  bigBlind: number;
  currentBet: number;
}) {
  const canRaise = maxRaiseTo > (callAmount > 0 ? (myCurrentBet + callAmount) : 0);
  const isAllInOnly = canRaise && minRaiseTo > maxRaiseTo;
  const effectiveMin = Math.min(minRaiseTo, maxRaiseTo);
  const clamped = isAllInOnly ? maxRaiseTo : Math.max(effectiveMin, Math.min(maxRaiseTo, raiseAmount));
  const showSlider = canRaise && !isAllInOnly && maxRaiseTo > effectiveMin;
  const isAllIn = isAllInOnly || (canRaise && clamped >= maxRaiseTo);

  const isPreflop = phase === 'preflop';
  const previousRaise = Math.max(bigBlind, currentBet);

  // Editable amount input
  const [inputStr, setInputStr] = useState(() => (clamped / 100).toFixed(2));
  const isEditingRef = useRef(false);

  // Reset to minimum bet at the start of each new betting round
  useEffect(() => {
    isEditingRef.current = false;
    setRaiseAmount(effectiveMin);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (!isEditingRef.current) {
      setInputStr((clamped / 100).toFixed(2));
    }
  }, [clamped]);

  const handleInputBlur = () => {
    isEditingRef.current = false;
    const dollars = parseFloat(inputStr);
    if (isNaN(dollars) || dollars * 100 < effectiveMin) {
      setRaiseAmount(effectiveMin);
    } else if (dollars * 100 >= maxRaiseTo) {
      setRaiseAmount(maxRaiseTo);
    } else {
      setRaiseAmount(Math.round(dollars * 100));
    }
  };

  const setPct = (pct: number) => {
    setRaiseAmount(Math.min(maxRaiseTo, Math.max(effectiveMin, myCurrentBet + Math.floor(pot * pct))));
  };

  const setMult = (mult: number) => {
    const target = Math.round(mult * previousRaise);
    setRaiseAmount(Math.min(maxRaiseTo, Math.max(effectiveMin, target)));
  };

  // +/- step by 1 big blind
  const decrement = () => setRaiseAmount((prev) => Math.max(effectiveMin, prev - bigBlind));
  const increment = () => setRaiseAmount((prev) => Math.min(maxRaiseTo, prev + bigBlind));

  const raiseBtnLabel = isAllIn ? 'All In' : callAmount === 0 ? 'Bet' : 'Raise';

  return (
    <div className="bg-black/80 backdrop-blur-md rounded-xl border border-white/[0.08] p-3 sm:p-4 shadow-[0_8px_40px_rgba(0,0,0,0.5)] w-[380px] max-w-full">
      {/* Your-turn indicator */}
      {isMyTurn && (
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-primary text-xs font-semibold uppercase tracking-wider">Your Turn</span>
        </div>
      )}

      {/* Preflop: multiplier presets | Postflop: percentage presets */}
      {showSlider && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {isPreflop ? (
            <>
              {[2.5, 3, 4, 5].map((mult) => (
                <button
                  key={mult}
                  type="button"
                  onClick={() => setMult(mult)}
                  disabled={!isMyTurn}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.06] text-text-muted text-xs font-medium hover:bg-white/[0.12] hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  {mult}x
                </button>
              ))}
            </>
          ) : (
            <>
              {[
                { label: '33%', pct: 0.33 },
                { label: '50%', pct: 0.5 },
                { label: '75%', pct: 0.75 },
                { label: '100%', pct: 1 },
              ].map(({ label, pct }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setPct(pct)}
                  disabled={!isMyTurn}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.06] text-text-muted text-xs font-medium hover:bg-white/[0.12] hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  {label}
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            onClick={() => setRaiseAmount(maxRaiseTo)}
            disabled={!isMyTurn}
            className="px-3 py-1.5 rounded-lg bg-red-600/30 text-red-300 text-xs font-bold hover:bg-red-600/50 hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            All In
          </button>
        </div>
      )}

      {/* Slider + amount input */}
      {showSlider && (
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={decrement}
            disabled={!isMyTurn}
            className="w-7 h-7 rounded-lg bg-white/[0.08] hover:bg-white/[0.16] flex items-center justify-center text-sm font-bold text-text disabled:opacity-30 transition-colors shrink-0"
          >
            −
          </button>
          <input
            type="range"
            min={effectiveMin}
            max={maxRaiseTo}
            step={1}
            value={clamped}
            onChange={(e) => setRaiseAmount(Number(e.target.value))}
            disabled={!isMyTurn}
            className="flex-1 h-2 rounded-full appearance-none bg-white/[0.08] accent-primary disabled:opacity-30 cursor-pointer"
          />
          <button
            type="button"
            onClick={increment}
            disabled={!isMyTurn}
            className="w-7 h-7 rounded-lg bg-white/[0.08] hover:bg-white/[0.16] flex items-center justify-center text-sm font-bold text-text disabled:opacity-30 transition-colors shrink-0"
          >
            +
          </button>
          {/* Editable amount field */}
          <div className="flex items-center rounded-lg bg-surface/80 border border-white/[0.08] overflow-hidden shrink-0">
            <span className="pl-2.5 text-primary font-bold text-sm leading-none">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={inputStr}
              disabled={!isMyTurn}
              onFocus={() => { isEditingRef.current = true; }}
              onChange={(e) => {
                const str = e.target.value;
                setInputStr(str);
                const cents = Math.round(parseFloat(str) * 100);
                if (!isNaN(cents)) {
                  if (cents >= effectiveMin && cents <= maxRaiseTo) setRaiseAmount(cents);
                  else if (cents > maxRaiseTo) setRaiseAmount(maxRaiseTo);
                }
              }}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                  room.send('raise', { amount: clamped });
                }
              }}
              className="w-20 pr-2.5 py-1.5 bg-transparent text-primary font-bold text-sm tabular-nums outline-none disabled:opacity-30"
            />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => room.send('fold')}
          disabled={!isMyTurn}
          className="flex-1 py-3 rounded-xl bg-gradient-to-b from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 text-white font-bold text-sm transition-all disabled:opacity-30 disabled:pointer-events-none shadow-lg shadow-red-900/30 active:scale-[0.97]"
        >
          Fold
        </button>
        <button
          type="button"
          onClick={() => room.send('call')}
          disabled={!isMyTurn}
          className="flex-1 py-3 rounded-xl bg-gradient-to-b from-cyan-600 to-cyan-800 hover:from-cyan-500 hover:to-cyan-700 text-white font-bold text-sm transition-all disabled:opacity-30 disabled:pointer-events-none shadow-lg shadow-cyan-900/30 active:scale-[0.97]"
        >
          <span className="block leading-tight">{callAmount === 0 ? 'Check' : 'Call'}</span>
          {callAmount > 0 && (
            <span className="block text-[14px] font-semibold text-white/80 leading-tight mt-0.5">
              ${(callAmount / 100).toFixed(2)}
            </span>
          )}
        </button>
        {canRaise && (
          <button
            type="button"
            onClick={() => room.send('raise', { amount: clamped })}
            disabled={!isMyTurn}
            className="flex-1 py-3 rounded-xl bg-gradient-to-b from-primary to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white font-bold text-sm transition-all disabled:opacity-30 disabled:pointer-events-none shadow-lg shadow-emerald-900/30 active:scale-[0.97]"
          >
            <span className="block leading-tight">{raiseBtnLabel}</span>
            <span className="block text-[14px] font-semibold text-white/80 leading-tight mt-0.5">
              ${(clamped / 100).toFixed(2)}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
